import { useContext, useMemo } from 'react';
import { DescriptorType, entityType, EntityTypeID, FieldVisibilityConfig, VisibilityConfig, WorkflowType } from '@fabric-ui/components';
import { GroupHeaderRowType } from '@fabric-ui/components/Table';
import { flattenObject, isDeepEqual, noop, QueryKey } from '@fabric-ui/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useGqlQuery } from '../../API/useAvQuery';
import { useAvContext } from '../../context/AvContextProvider';
import { NotificationContext } from '../../context/AvSnackBarProvider';
import { UNKNOWN_ERROR_MESSAGE } from '../../hooks/useHandleError';
import useQueryObject from '../../hooks/useQueryObjectSql';
import { getGraphQLError } from '../../utils/graphqlUtils';
import { flattenKeys, getNetworkErrorCode } from '../../utils/utils';
import { ProjEvaluationsData, TFieldRegulation, TFieldRegulationData, TGetRowsProps } from './types';

const GET_ACCOUNT_REGULATIONS_DATA = gql`
  query getFieldRegulationsData {
    getFieldRegulationsData {
      fieldRegulations {
        id
        projectionName
        builtInProjection
        fieldName
        config
      }
      availableUpdateRolesPerProjection {
        projectionName
        availableRoles
      }
    }
  }
`;

export const useFieldRegulationsData = () =>
  useGqlQuery<TFieldRegulationData>({
    queryKey: [QueryKey.FieldRegulations],
    query: GET_ACCOUNT_REGULATIONS_DATA,
  });

const CREATE_MANUAL_UPDATE = gql`
  mutation createFieldRegulation($fieldRegulation: FieldRegulationDTOInput!) {
    createFieldRegulation(fieldRegulation: $fieldRegulation) {
      projectionName
      builtInProjection
      fieldName
      config
    }
  }
`;
const UPDATE_MANUAL_UPDATE = gql`
  mutation updateFieldRegulation($fieldRegulation: FieldRegulationDTOInput!) {
    updateFieldRegulation(fieldRegulation: $fieldRegulation) {
      projectionName
    }
  }
`;
export const useSaveManualUpdates = () => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const client = useQueryClient();
  const { mutate, isPending: isLoading } = useMutation<() => Promise<any>, unknown, TFieldRegulation>({
    mutationFn: fieldRegulation => api(fieldRegulation.id ? UPDATE_MANUAL_UPDATE : CREATE_MANUAL_UPDATE, { options: { fieldRegulation } }),
    onSuccess: () => {
      enqueueSnackbar('Saved Successfully', { variant: 'success' });
      return client.resetQueries({ queryKey: ['fieldRegulations'] });
    },
  });
  return { saveManualUpdates: mutate, isLoading };
};

const GET_PROJ_EVALUATIONS = gql`
  query projEvaluations {
    projEvaluations {
      projId {
        name
        builtIn
      }
      config {
        fieldAggregations {
          aggName
          args {
            stringValue
            argumentAlias
          }
          func
          filter
          builtIn
        }
        fieldScripts {
          field {
            name
            type
            origin
          }
          aggNames
          script
          fallbackStrategy
          evalStrategy
          aggregationSteps
        }
      }
    }
    aggProjections {
      meta {
        id {
          name
          builtIn
        }
      }
      aggregates {
        name
        builtIn
      }
      clusteringPredicate {
        anyofList {
          fieldsList
          ignoreNulls
        }
      }
    }
    aggregationFunctionDefinitions {
      value: funcType
      title: funcType
      argNames
    }
  }
`;

export const useProjEvaluations = (gcTime: number = 0) => {
  const { data: origData, ...rest } = useGqlQuery<ProjEvaluationsData>({
    queryKey: [QueryKey.ProjEvaluations],
    query: GET_PROJ_EVALUATIONS,
    select: (_, data) => data,
    gcTime,
  });

  const data = useMemo(
    () => ({
      data: origData?.projEvaluations.map(projEval => ({
        ...projEval,
        proj: origData?.aggProjections.find(({ meta: { id } }) => isDeepEqual(id, projEval.projId)),
      })),
      funcTypes: origData?.aggregationFunctionDefinitions,
    }),
    [origData]
  );

  return { ...rest, data };
};

const RUN = gql`
  mutation runAggregations($projIds: [ProjectionIDInput!]!, $executionPlan: EtlExecutionPlan) {
    runAggregations(projIds: $projIds, executionPlan: $executionPlan) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

export const useRunAggregation = () => {
  const { api } = useAvContext();
  const { setRunInfo = noop } = useContext(NotificationContext);

  return useMutation<unknown, unknown, { projIds: { name: string; builtIn: boolean }[]; executionPlan?: any }>({
    mutationFn: ({ projIds, executionPlan }) =>
      api(RUN, { options: { projIds, executionPlan } })
        .then(({ data }) =>
          setRunInfo({
            runId: data.runAggregations.workflowRun.runId,
            wfId: data.runAggregations.workflowRun.id, // TODO change id to wfId and remove .workflowRun
            returnStatusOnly: true,
            workflowType: WorkflowType.ReprocessAggProj,
          })
        )
        .catch(error => {
          if (![400, 429].includes(getNetworkErrorCode(error))) {
            throw error;
          }
        }),
  });
};

export const shouldForceComment = (fieldRegulations, fields, projectionName) => {
  if (!fields) {
    return {};
  }
  const fieldsFlatten = flattenKeys(fields);
  const regulations = fieldRegulations
    .filter(r => r.projectionName === projectionName && fieldsFlatten.some(field => field === r.fieldName))
    .filter(f => f.config.forceComment);
  if (regulations.some(r => r.config.forceCommentType === 'ALWAYS')) {
    return { fields: regulations.map(r => r.fieldName), condition: true };
  }
  const customRegulations = regulations.filter(r => r.config.forceCommentType === 'CUSTOM');
  const flattenFields = flattenObject({ obj: fields });
  return {
    fields: regulations.map(r => r.fieldName),
    condition: !!(
      customRegulations.length &&
      customRegulations.some(r => flattenFields[r.fieldName] && r.config.forceCommentValues.includes(flattenFields[r.fieldName]))
    ),
  };
};

export const getFieldRegulation = (fieldRegulations, projectionName, field) =>
  fieldRegulations?.find(regulation => regulation.projectionName === projectionName && regulation.fieldName === field);

export const getContentValidationValues = (field, projectionName, fieldRegulations) => {
  const fieldRegulation = getFieldRegulation(fieldRegulations, projectionName, field);
  return fieldRegulation?.config?.contentValidation ? fieldRegulation?.config?.contentValidationAllowedValues.sort() : [];
};

export const getContentValidationQueryObject = (field, projectionName, fieldRegulations) => {
  const fieldRegulation = getFieldRegulation(fieldRegulations, projectionName, field);
  return {
    contentValidation: fieldRegulation?.config?.contentValidation,
    queryObject: fieldRegulation?.config?.queryObject,
    isQueryInUse: fieldRegulation?.config?.useQueryForContentValidation,
  };
};

export const getIsManualOverrideEnabled = (field, projectionName, fieldRegulations) => {
  const config = getFieldRegulation(fieldRegulations, projectionName, field)?.config;
  return config?.allowManualUpdate && config?.allowedForUser;
};

export const useContentValidationValues = ({ field, projectionName }: { field: string; projectionName: string }) => {
  const { data: { fieldRegulations } = {} } = useFieldRegulationsData();
  const { contentValidation, queryObject, isQueryInUse } = getContentValidationQueryObject(field, projectionName, fieldRegulations);
  const contentValidationValues = isQueryInUse ? [] : getContentValidationValues(field, projectionName, fieldRegulations);
  const isContentValidation = !!(contentValidation || contentValidationValues?.length);
  const { data: queryObjectValues, isLoading } = useQueryObject({
    queryObject,
    onSuccess: d => d.map(({ id, ...rest }) => Object.values(rest)[0]).filter(v => !!v),
    options: { enabled: !!(isContentValidation && queryObject && isQueryInUse), gcTime: 0 },
  });

  return {
    isContentValidation,
    values: (isQueryInUse && queryObject && isLoading) || !isContentValidation ? [] : queryObjectValues || contentValidationValues,
  };
};

export const getFieldKey = v => (Array.isArray(v) ? JSON.stringify(v) : v);
export const fieldTypeLabelMap = {
  [DescriptorType.TYPE_STRING]: { label: 'Text', pyType: 'str' },
  [`[${DescriptorType.TYPE_STRING}]`]: { label: 'Repeated Text', pyType: 'list[str]' },
  [DescriptorType.TYPE_DOUBLE]: { label: 'Number', pyType: 'float' },
  [`[${DescriptorType.TYPE_DOUBLE}]`]: { label: 'Repeated Number', pyType: 'list[float]' },
  [DescriptorType.TYPE_BOOL]: { label: 'Boolean', pyType: 'bool' },
  [`[${DescriptorType.TYPE_BOOL}]`]: { label: 'Repeated Boolean', pyType: 'list[bool]' },
  [DescriptorType.TYPE_INT32]: { label: 'Number', pyType: 'int' },
  [`[${DescriptorType.TYPE_INT32}]`]: { label: 'Repeated Number', pyType: 'list[int]' },
  [DescriptorType.TYPE_UINT32]: { label: 'Number', pyType: 'int' },
  [`[${DescriptorType.TYPE_UINT32}]`]: { label: 'Repeated Number', pyType: 'list[int]' },
  [DescriptorType.TYPE_ENUM]: { label: 'Text', pyType: 'str' },
  [`[${DescriptorType.TYPE_ENUM}]`]: { label: 'Repeated Text', pyType: 'list[str]' },
  DATE: { label: 'Date', pyType: 'object' },
  '["DATE"]': { label: 'Repeated Date', pyType: 'list[object]' },
  DATETIME: { label: 'Date', pyType: 'object' },
  '["DATETIME"]': { label: 'Repeated Date', pyType: 'list[object]' },
  MESSAGE: { label: 'Message', pyType: 'dict' },
  '["MESSAGE"]': { label: 'Repeated Message', pyType: 'list[dict]' },
  IP: { label: 'IP', pyType: 'str' },
  '["IP"]': { label: 'Repeated IP', pyType: 'list[str]' },
  [entityType]: { label: 'Entity' },
};
export const getPythonType = type => fieldTypeLabelMap[getFieldKey(type)]?.pyType || 'str';

const GET_MODEL_PREVIEW = gql`
  query previewSpf($evals: AggProjectionEvaluationInput!, $selectivityFilter: FilterScalar, $fieldName: String) {
    previewSpf(aggProjectionEvaluation: $evals, selectivityFilter: $selectivityFilter, fieldName: $fieldName) {
      internalError
      scriptError
      previewData
    }
  }
`;
export const useModelPreview = ({ field, evals, selectivityFilter = {} }, select = d => d) =>
  useGqlQuery({
    queryKey: [QueryKey.ModelPreview, selectivityFilter, field],
    query: GET_MODEL_PREVIEW,
    variables: { evals, selectivityFilter, fieldName: field },
    select,
    enabled: !!evals,
    gcTime: 0,
  });

export const GROUP_ROW_ID_PREFIX = `GROUP_ROW_`;

export type ModelRowItem = {
  id: string;
  displayName: string;
  fullDisplayName?: string;
  projectionName: string;
  systemName: string;
  entityTypeId: EntityTypeID;
  type: number | string | number[];
  builtIn?: boolean;
  builtInProjection: boolean;
  isMetadataField?: boolean;
  function?: string;
  groupHeaderType?: GroupHeaderRowType;
  hasManualOverride?: boolean;
  tags?: string[];
  visibilityConfig?: VisibilityConfig;
  isSupportedInBasicHistorical?: boolean;
};
export const getRows = ({ projOptions, fieldRegulations, search }: TGetRowsProps) => {
  const rows = projOptions.reduce((acc, entity) => {
    const result: ModelRowItem[] = [
      ...acc,
      {
        id: entity.projDisplayName,
        entityTypeId: entity.entityTypeId,
        displayName: entity.projDisplayName,
        projectionName: entity.projId.name,
        systemName: entity.projId.name,
        builtInProjection: entity.projId.builtIn,
        type: entityType,
        groupHeaderType: GroupHeaderRowType.GROUP,
      },
    ];

    const seenGroupNames = new Map<string, ModelRowItem[]>();

    entity.fields.forEach(
      ({ id, name: field, displayName, builtIn, tags, isMetadata, type, visibilityConfig, groupName, isSupportedInBasicHistorical }) => {
        const fieldRow = {
          id: id || `${entity.projDisplayName}.${field}`.toLowerCase(),
          entityTypeId: entity.entityTypeId,
          displayName: displayName || field,
          fullDisplayName: `${entity.projDisplayName} ${displayName || field}`,
          visible: !visibilityConfig.config.hidden,
          projectionName: entity.projId.name,
          systemName: field,
          type: ['number', 'string'].includes(typeof type) ? type : entity.typeMap[id || `${entity.projId.name}.${field}`.toLowerCase()],
          builtIn,
          builtInProjection: entity.projId.builtIn,
          isMetadataField: isMetadata,
          visibilityConfig,
          isSupportedInBasicHistorical,
          function: '',
          hasManualOverride: fieldRegulations?.find(
            regulation => regulation.projectionName === entity.projId.name && regulation.fieldName === field
          )?.config.allowManualUpdate,
          tags,
        } as ModelRowItem;

        if (groupName) {
          if (!seenGroupNames.get(groupName)) {
            seenGroupNames.set(groupName, [
              {
                id: `${GROUP_ROW_ID_PREFIX}_${groupName}`,
                entityTypeId: entity.entityTypeId,
                displayName: groupName,
                projectionName: entity.projId.name,
                systemName: groupName,
                builtInProjection: entity.projId.builtIn,
                type: entityType,
                groupHeaderType: GroupHeaderRowType.SUB_GROUP,
              },
            ]);
          }
          seenGroupNames.set(groupName, [...(seenGroupNames.get(groupName) as ModelRowItem[]), fieldRow]);
        } else {
          result.push(fieldRow);
        }
      }
    );

    return [...result, ...Array.from(seenGroupNames.values()).flat()];
  }, [] as ModelRowItem[]);

  return search
    ? rows.filter(r => !r.groupHeaderType && (r.fullDisplayName!.toLowerCase().includes(search) || r.id.toLowerCase().includes(search)))
    : rows;
};
const handleVisibilityError = (enqueueSnackbar, onError?) => e => {
  if ([e?.status, getGraphQLError(e)?.extensions?.status, e?.extensions?.status].includes(400)) {
    console.warn(e);
  } else {
    console.error(e);
  }
  onError?.();
  enqueueSnackbar(typeof e?.message === 'string' ? e?.message : UNKNOWN_ERROR_MESSAGE, {
    variant: 'error',
  });
};

const CREATE_FIELD_VISIBILITY = gql`
  mutation createFieldVisibility($fieldVisibility: FieldVisibilityDtoInput!) {
    createFieldVisibility(fieldVisibility: $fieldVisibility) {
      id
    }
  }
`;

const UPDATE_FIELD_VISIBILITY = gql`
  mutation updateFieldVisibility($fieldVisibility: FieldVisibilityDtoInput!) {
    updateFieldVisibility(fieldVisibility: $fieldVisibility) {
      id
    }
  }
`;

export const useUpdateFieldVisibility = onError => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (fieldVisibility: VisibilityConfig) =>
      api(fieldVisibility.id ? UPDATE_FIELD_VISIBILITY : CREATE_FIELD_VISIBILITY, {
        options: { fieldVisibility },
        muteErrors: true,
      }).catch(handleVisibilityError(enqueueSnackbar, onError)),
  });
};

const UPDATE_FIELD_VISIBILITIES = gql`
  mutation updateFieldVisibilities($fieldVisibilities: [FieldVisibilityDtoInput!], $config: FieldVisibilityConfigDto!) {
    updateFieldVisibilities(fieldVisibilities: $fieldVisibilities, config: $config) {
      id
    }
  }
`;

export const useUpdateFieldVisibilities = () => {
  const {
    api,
    accountEntities: { refetch },
  } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      fieldVisibilities,
      config,
    }: {
      fieldVisibilities: Pick<VisibilityConfig, 'projectionName' | 'builtInProjection' | 'fieldName'>[];
      config: FieldVisibilityConfig;
    }) =>
      api(UPDATE_FIELD_VISIBILITIES, {
        options: { fieldVisibilities, config },
        onSuccess: () => {
          refetch();
          return enqueueSnackbar(`Field visibility updated successfully for ${fieldVisibilities.length} fields`, { variant: 'success' });
        },
        muteErrors: true,
      }).catch(handleVisibilityError(enqueueSnackbar)),
  });
};
